import { autoUpdate, flip, offset, ReferenceType, safePolygon, shift, useDismiss, useFloating, useHover, useInteractions } from '@floating-ui/react';
import { Layers2 } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'utils/cn';
import { TaskCluster } from '../../hooks/use-task-clustering';
import { useAppSelector } from '../../store';
import { getSelectedTaskId } from '../../store/tasksSlice';
import { AiSticker } from './ai-sticker';
import { Sticker } from './sticker';

interface ClusterStickerProps {
    cluster: TaskCluster;
    onToggle: (clusterId: string) => void;
    hasFixedAncestor?: boolean;
    shouldAnimateCluster: (clusterId: string) => boolean;
    onAnimationComplete: (clusterId: string) => void;
    taskPanelSetReference?: (node: ReferenceType | null) => void;
}

export const ClusterSticker = ({
    cluster,
    hasFixedAncestor = false,
    shouldAnimateCluster,
    onAnimationComplete,
    taskPanelSetReference
}: ClusterStickerProps) => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const selectedTaskId = useAppSelector(getSelectedTaskId);

    const [isHovered, setIsHovered] = useState(false);
    const isExpanded = cluster.isExpanded || isHovered;

    const { refs, context } = useFloating({
        open: isExpanded,
        middleware: [
            offset(16),
            flip({
                padding: 16,
                fallbackPlacements: [
                    'bottom', 'bottom-start', 'bottom-end',
                    'top-start', 'top-end',
                    'right-start', 'right-end',
                    'left-start', 'left-end',
                ],
                fallbackStrategy: 'bestFit',
            }),
            shift({
                padding: 16,
            }),
        ],
        placement: 'bottom',
        whileElementsMounted: autoUpdate,
    });

    const hover = useHover(context, {
        restMs: 500,
        move: false,
        handleClose: safePolygon({
            blockPointerEvents: true,
        }),
    });

    const dismiss = useDismiss(context, {
        referencePress: true,
        ancestorScroll: true,
        bubbles: {
            outsidePress: true,
            escapeKey: true,
        }
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover,
        dismiss,
    ]);

    const handleMouseEnter = useCallback(() => {
        setIsHovered(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsHovered(false);
    }, []);

    const getGridLayout = () => {
        const itemsPerRow = Math.min(5, cluster.tasks.length);
        const rows = Math.ceil(cluster.tasks.length / itemsPerRow);

        return {
            display: 'grid',
            gridTemplateColumns: `repeat(${itemsPerRow}, 1fr)`,
            gridTemplateRows: `repeat(${rows}, 1fr)`,
            gap: '8px',
            padding: '8px',
        };
    };

    const getCompositionInfo = () => {
        const aiTaskCount = cluster.tasks.filter(
            (task) => 'isAiTask' in task && task.isAiTask
        ).length;
        const regularTaskCount = cluster.tasks.length - aiTaskCount;

        return { aiTaskCount, regularTaskCount, total: cluster.tasks.length };
    };

    const composition = getCompositionInfo();
    const shouldAnimate = shouldAnimateCluster(cluster.id);
    const [animationComplete, setAnimationComplete] = useState(false);

    const animationCompleteCountRef = useRef(0);
    const totalStickersRef = useRef(cluster.tasks.length);

    const handleStickerAnimationComplete = useCallback(() => {
        animationCompleteCountRef.current += 1;
        if (
            animationCompleteCountRef.current >= totalStickersRef.current &&
            shouldAnimate &&
            !animationComplete
        ) {
            setAnimationComplete(true);
            onAnimationComplete(cluster.id);
        }
    }, [shouldAnimate, animationComplete, onAnimationComplete, cluster.id]);

    useEffect(() => {
        if (isExpanded && shouldAnimate) {
            animationCompleteCountRef.current = 0;
            totalStickersRef.current = cluster.tasks.length;
            setAnimationComplete(false);
        }
    }, [isExpanded, shouldAnimate, cluster.tasks.length]);



    // Clamp horizontal cluster position to viewport to avoid overflowed containers like carousels
    const clampClusterLeft = useCallback((left: number) => {
        const H_MARGIN = 16;
        const viewportWidth = window.innerWidth;
        const scrollX = window.scrollX || document.documentElement.scrollLeft;
        const stickerWidth = containerRef.current?.offsetWidth ? Math.min(containerRef.current.offsetWidth, 32) : 32;
        const minLeft = hasFixedAncestor ? H_MARGIN : scrollX + H_MARGIN;
        const maxLeft = (hasFixedAncestor ? 0 : scrollX) + viewportWidth - stickerWidth - H_MARGIN;
        return Math.max(minLeft, Math.min(left, maxLeft));
    }, [hasFixedAncestor]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
                position: hasFixedAncestor ? 'fixed' : 'absolute',
                top: cluster.position.top,
                left: clampClusterLeft(cluster.position.left),
                zIndex: isExpanded ? 40 : 10,
                pointerEvents: 'auto',
                cursor: 'pointer',
            }}
            ref={(el) => {
                containerRef.current = el as HTMLDivElement | null;
                refs.setReference(el as ReferenceType);
            }}
            onAnimationComplete={handleStickerAnimationComplete}
        >

            <motion.div
                {...getReferenceProps}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={cn(
                    'relative flex w-8 h-8 items-center justify-center rounded-[15%_50%_50%_50%] shadow-md outline outline-2 outline-white bg-secondary text-white hover:bg-secondary/80 ',
                    isExpanded && 'w-auto h-auto rounded-tl-[8px] rounded-bl-[28px] rounded-br-[28px] rounded-tr-[28px] bg-secondary/60 backdrop-blur-sm p-1'
                )}
                aria-label={t(
                    'cluster_sticker.aria.cluster_count_mixed',
                    'Task cluster with {{total}} tasks ({{regular}} regular, {{ai}} AI)',
                    {
                        total: composition.total,
                        regular: composition.regularTaskCount,
                        ai: composition.aiTaskCount,
                    }
                )}
            >
                <AnimatePresence>
                    {!isExpanded ? (
                        <>
                            <Layers2 size={18} focusable="false" tabIndex={-1} className='pointer-events-none select-none' />
                            <span className="bg-tertiary flex min-h-4 px-1 items-center justify-center rounded-full align-middle text-xs leading-none text-white absolute -top-[3px] -right-[3px]">
                                {cluster.tasks.length}
                            </span>
                        </>
                    ) : (
                        <div
                            ref={refs.setFloating}
                            {...getFloatingProps}
                            style={{
                                zIndex: 1,
                                pointerEvents: 'none',
                            }}
                            className="pointer-events-none relative"
                        >
                            <motion.div
                            >
                                <div
                                    className="pointer-events-none overflow-hidden"
                                    style={getGridLayout()}
                                >
                                    {cluster.tasks.map((task, index) => {
                                        const key = `cluster-${cluster.id}-task-${task.task_comment_id || task.task_id}-${index}`;
                                        const delay = shouldAnimate ? index * 0.1 : 0; // seconds for framer motion

                                        if ('isAiTask' in task && task.isAiTask) {
                                            return (

                                                <motion.div
                                                    key={key}
                                                    className="relative pointer-events-auto"
                                                    initial={{ x: -100, opacity: 0 }}
                                                    animate={{ x: 0, opacity: 1 }}
                                                    transition={{
                                                        duration: 0.2,
                                                        delay: (index - 1) * 0.1,
                                                        ease: "easeOut",
                                                        type: "spring",
                                                        stiffness: 300,
                                                        damping: 25,
                                                    }} >

                                                    <AiSticker
                                                        containerRef={containerRef}
                                                        aiTask={task.aiTaskData}
                                                        task={task}
                                                        positioned={false}
                                                        inClusterPanel={true}
                                                        shouldAnimate={shouldAnimate}

                                                    />
                                                </motion.div>

                                            );
                                        }

                                        return (
                                            <motion.div key={key} className="relative pointer-events-auto" initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ delay, type: "spring", stiffness: 300, damping: 25 }}>
                                                <Sticker
                                                    containerRef={containerRef}
                                                    draggable={false}
                                                    positioned={false}
                                                    task={task}
                                                    dummyTask={false}
                                                    isActiveSticker={false}
                                                    overridePosition={null}
                                                    isClusterExpanded={true}
                                                    inClusterPanel={true}
                                                    shouldAnimate={shouldAnimate}
                                                    taskPanelSetReference={
                                                        selectedTaskId === task.task_id ||
                                                            (task.task_comment_id &&
                                                                selectedTaskId &&
                                                                cluster.tasks.find((t) => t.task_id === selectedTaskId)?.task_comment_id === task.task_comment_id)
                                                            ? taskPanelSetReference
                                                            : undefined
                                                    }
                                                />
                                            </motion.div>
                                        );
                                    })}

                                </div>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>
            </motion.div>

        </motion.div>
    );
};